import { App } from "@capacitor/app";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useRegisterPushTokenMutation } from "@/lib/api/apiSlice";
import { isAndroid, isIOS, isNativePlatform } from "@/lib/capacitor";
import { initPushNotifications } from "@/lib/capacitor/push-notifications";

const STATUS_BAR_COLOR = "#FDFAF7";

async function configureNativeChrome(): Promise<void> {
  document.body.classList.add("capacitor-native");
  if (isIOS()) {
    document.body.classList.add("capacitor-ios");
  } else if (isAndroid()) {
    document.body.classList.add("capacitor-android");
  }

  await StatusBar.setStyle({ style: Style.Light });

  if (isIOS()) {
    await StatusBar.setOverlaysWebView({ overlay: true });
  } else if (isAndroid()) {
    await StatusBar.setBackgroundColor({ color: STATUS_BAR_COLOR });
  }

  await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
  await Keyboard.setScroll({ isDisabled: false });
}

export default function CapacitorApp() {
  const { status, data: session } = useAuth();
  const [registerPushToken] = useRegisterPushTokenMutation();
  const lastRegisteredTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isNativePlatform()) {
      return;
    }

    void configureNativeChrome();
  }, []);

  useEffect(() => {
    if (!isNativePlatform() || status === "loading") {
      return;
    }

    void SplashScreen.hide();
  }, [status]);

  useEffect(() => {
    if (!isNativePlatform() || status !== "authenticated" || !session?.user) {
      return;
    }

    let teardownPush: (() => void) | undefined;

    void initPushNotifications(({ token, platform }) => {
      if (lastRegisteredTokenRef.current === token) {
        return;
      }

      lastRegisteredTokenRef.current = token;
      void registerPushToken({ token, platform });
    }).then((teardown) => {
      teardownPush = teardown;
    });

    return () => {
      teardownPush?.();
    };
  }, [registerPushToken, session?.user, status]);

  useEffect(() => {
    if (!isNativePlatform()) {
      return;
    }

    const backListener = App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
        return;
      }

      void App.minimizeApp();
    });

    const resumeListener = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        document.body.classList.remove("app-backgrounded");
        void configureNativeChrome();
        return;
      }

      document.body.classList.add("app-backgrounded");
    });

    return () => {
      void backListener.then((handle) => handle.remove());
      void resumeListener.then((handle) => handle.remove());
    };
  }, []);

  return null;
}
